import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";
import {
  useAddSubAdminMutation,
  useLazyGetSubAdminByIdQuery,
  useUpdateSubAdminMutation,
} from "../../services/subAdmin";
import { useLazyGetRoleQuery } from "../../services/role";
import { showError, showToast } from "../../constants/toasts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/mediaUpload";
import PhoneInput from "react-phone-input-2";

interface SubAdminFormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  country_code: string;
  role_id: number | string;
  sub_role?: number | string;
}

const AddSubadmin = () => {
  const [profileImage, setProfileImage] = useState<string>("");
  const [imageId, setImageId] = useState<number | null>(null);
  const [imageError, setImageError] = useState<string>("");
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [addSubAdmin] = useAddSubAdminMutation();
  const [updateSubAdmin] = useUpdateSubAdminMutation();
  const [getSubAdminById] = useLazyGetSubAdminByIdQuery();
  const [getRoles] = useLazyGetRoleQuery();
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [subAdmin, setSubAdmin] = useState<any>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [page, setPage] = useState(1);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const fetchRoles = async () => {
    try {
      const response = await getRoles({
        length: 10,
        start: page,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response?.status === 200) {
        setRoles(response.data || []);
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message);
    }
  };
  const fetchSubAdminById = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const response = await getSubAdminById({ id: numericId }).unwrap();
      if (response?.status === 200 || response?.statusCode === 200) {
        setSubAdmin(response.data);
      }
    } catch (error: any) {
      showError(error?.message || error?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    if (isEditMode) {
      fetchSubAdminById();
    }
  }, [id]);

  useEffect(() => {
    if (subAdmin?.profile_picture?.media_file_url) {
      setProfileImage(subAdmin.profile_picture.media_file_url);
      setImageError("");
      setIsFormSubmitted(false);
    }
  }, [subAdmin]);

  const formik = useFormik<SubAdminFormValues>({
    enableReinitialize: true,
    initialValues: {
      first_name: subAdmin?.first_name || "",
      last_name: subAdmin?.last_name || "",
      email: subAdmin?.email || "",
      phone_number: subAdmin?.phone_number || "",
      country_code: subAdmin?.country_code?.replace("+", "") || "",
      // Prefill using sub_role_id when editing; fallback to role_id if available
      role_id: subAdmin?.sub_role_id ?? subAdmin?.role_id ?? "",
      // Backend expects sub_role as id; use sub_role_id
      sub_role: subAdmin?.sub_role_id ?? "",
    } as SubAdminFormValues,
    validationSchema: Yup.object({
      first_name: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
        .min(2, "At least 2 characters")
        .max(50, "Maximum 50 characters allowed")
        .required("Required"),
      last_name: Yup.string()
        .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
        .min(2, "At least 2 characters")
        .max(50, "Maximum 50 characters allowed")
        .required("Required"),
      email: Yup.string()
        .email("Invalid email")
        .max(50, "Maximum 50 characters allowed")
        .required("Required"),
      phone_number: Yup.string()
        .matches(/^[0-9]{7,15}$/i, "Enter a valid number")
        .required("Required to fill"),
      country_code: Yup.string().required("Required to fill"),
      role_id: Yup.number().required("Required"),
    }),
    onSubmit: async (values) => {
      setIsFormSubmitted(true);
      
      // Check for image validation error
      if (imageError) {
        showError(imageError);
        return;
      }
      // Require profile image
      if (!imageId && !subAdmin?.profile_picture?.id) {
        setImageError("Image is required");
        showError("Image is required");
        return;
      }
      const body: any = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone_number: values.phone_number,
        country_code: `+${values.country_code}`,
        sub_role: values.sub_role,
      };

      if (imageId) {
        body.profile_picture = imageId;
      } else if (subAdmin?.profile_picture?.id) {
        body.profile_picture = subAdmin.profile_picture.id;
      }
      try {
    
        setLoading(true);
        let response;

        if (isEditMode) {
          const numericId = Number(id);
          if (Number.isNaN(numericId)) return;
          response = await updateSubAdmin({ id: numericId, body }).unwrap();
        } else {
          response = await addSubAdmin({ body }).unwrap();
        }

        if (response?.status === 200 || response?.statusCode === 200) {
          showToast(
            isEditMode
              ? "Subadmin updated successfully"
              : "Subadmin added successfully"
          );
          navigate("/manage-subadmin");
        }
      } catch (error: any) {
        showError(
          error?.data?.message ||
            `Failed to ${isEditMode ? "update" : "add"} subadmin`
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!subAdmin) return;
    formik.setValues({
      first_name: subAdmin.first_name || "",
      last_name: subAdmin.last_name || "",
      email: subAdmin.email || "",
      phone_number: subAdmin.phone_number || "",
      country_code: (subAdmin.country_code || "").replace("+", ""),
    
      role_id: subAdmin.sub_role_id ?? subAdmin.role_id ?? "",
      sub_role: subAdmin.sub_role_id ?? "",
    } as SubAdminFormValues);
    if (subAdmin.profile_picture?.media_file_url) {
      setProfileImage(subAdmin.profile_picture.media_file_url);
    }
    if (subAdmin.profile_picture?.id) {
      setImageId(subAdmin.profile_picture.id);
    }
    // Clear any image errors when loading existing subAdmin data
    setImageError("");
    setIsFormSubmitted(false);
  }, [subAdmin]);

  // Handler to restrict name fields to alphabets only
  const handleNameChange = (
    field: "first_name" | "last_name",
    value: string
  ) => {
    // Allow only alphabets and spaces, limit to 50 characters
    const filteredValue = value
      .replace(/[^A-Za-z\s]/g, "")
      .substring(0, 50);
    formik.setFieldValue(field, filteredValue);
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;
    if (!file) {
      setProfileImage("");
      setImageId(null);
      setImageError("");
      return;
    }

    // Validate image format
    const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    const isValidFormat =
      allowedFormats.includes(fileType) ||
      fileExtension === "jpg" ||
      fileExtension === "jpeg" ||
      fileExtension === "png";

    if (!isValidFormat) {
      setImageError(
        "Invalid image format. Please upload a valid file in JPG, PNG, or JPEG format."
      );
      setProfileImage("");
      setImageId(null);
      event.target.value = "";
      return;
    }

    // Clear any previous errors
    setImageError("");
    setIsFormSubmitted(false);

    const response = (await UploadMedia(file)) as any;
    if (
      response?.data &&
      Array.isArray(response.data) &&
      response.data.length > 0
    ) {
      const uploaded = response.data[0];
      if (uploaded.media_file_url && uploaded.id) {
        setProfileImage(uploaded.media_file_url);
        setImageId(uploaded.id);
      }
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit Subadmin" : "Add Subadmin"}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-subadmin")}>
            Subadmin Management
          </span>{" "}
          - {isEditMode ? "Edit" : "Add"}
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group">
              <label>Upload Image</label>
              {profileImage ? (
                <div className="upload_image ">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={profileImage}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setProfileImage("");
                        setImageId(null);
                        setImageError("");
                        setIsFormSubmitted(false);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image " htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg,image/jpg",
                    }}
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
              {(imageError ||
                (isFormSubmitted &&
                  !imageId &&
                  !subAdmin?.profile_picture?.id)) && (
                <div
                  style={{
                    color: "red",
                    fontSize: "0.875rem",
                    marginTop: "8px",
                  }}
                >
                  {imageError || "Image is required"}
                </div>
              )}
            </div>
            <div className="control_group w_33">
              <label>First Name</label>
              <TextField
                name="first_name"
                placeholder="First Name"
                hiddenLabel
                fullWidth
                value={formik.values.first_name}
                onChange={(e) => handleNameChange("first_name", e.target.value)}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 50 }}
                error={
                  formik.touched.first_name && Boolean(formik.errors.first_name)
                }
                helperText={
                  formik.touched.first_name &&
                  (formik.errors.first_name as string)
                }
              />
            </div>
            <div className="control_group w_33">
              <label>Last Name</label>
              <TextField
                name="last_name"
                placeholder="Last Name"
                hiddenLabel
                fullWidth
                value={formik.values.last_name}
                onChange={(e) => handleNameChange("last_name", e.target.value)}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 50 }}
                error={
                  formik.touched.last_name && Boolean(formik.errors.last_name)
                }
                helperText={
                  formik.touched.last_name &&
                  (formik.errors.last_name as string)
                }
              />
            </div>
            <div className="control_group w_33">
              <label>Email</label>
              <TextField
                name="email"
                placeholder="Email"
                hiddenLabel
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 50 }}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={
                  formik.touched.email && (formik.errors.email as string)
                }
              />
            </div>
            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={`${formik.values.country_code}${formik.values.phone_number}`}
                country={"in"}
                inputClass="phoneInput"
                onChange={(value: string, country: any) => {
                  const dialCode = country?.dialCode || "";
                  const phoneNumber = value.replace(dialCode, "");
                  formik.setFieldValue("phone_number", phoneNumber);
                  formik.setFieldValue("country_code", dialCode);
                }}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              {(formik.touched.phone_number && formik.errors.phone_number) ||
              (formik.touched.country_code && formik.errors.country_code) ? (
                <div
                  className="error_text"
                  style={{ color: "red", fontSize: "0.875rem", marginTop: "8px" }}
                >
                  {(formik.errors as any).phone_number ||
                    (formik.errors as any).country_code}
                </div>
              ) : null}
            </div>
            <div className="control_group w_33">
              <label>Role</label>
              <Select
                fullWidth
                name="role_id"
                value={formik.values.role_id || ""}
                displayEmpty
                renderValue={(selected: any) => {
                  if (selected === "" || selected === null || selected === undefined) {
                    return "Select Role";
                  }
                  return roles.find((r) => r.id === selected)?.role_name || "Select Role";
                }}
                onChange={(e) => {
                  formik.setFieldValue("role_id", e.target.value);
                  formik.setFieldValue("sub_role", e.target.value);
                }}
                onBlur={() => formik.setFieldTouched("role_id", true)}
              >
                <MenuItem disabled value="">
                  Select Role
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.role_name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.role_id && formik.errors.role_id ? (
                <div className="error_text">{formik.errors.role_id as any}</div>
              ) : null}
            </div>
            {/* Password fields removed as per requirements */}
          </div>
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={loading}
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Adding..."
                : isEditMode
                ? "Update Subadmin"
                : "Add Subadmin"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddSubadmin;
